import React from 'react';
import { Check, ArrowRight, Train, Gauge, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const RailSystemsPage = () => {
  const specs = [
    { label: 'Chemistry', value: 'Multi-Ion' },
    { label: 'Voltage', value: 'Custom (up to 800V)' },
    { label: 'Capacity', value: 'Application specific' },
    { label: 'Cycle Life', value: '5,000+ cycles' },
    { label: 'Operating Temp', value: '-40°C to +65°C' },
    { label: 'Compliance', value: 'Railway standards' },
    { label: 'Enclosure', value: 'Fire-resistant' },
    { label: 'Warranty', value: 'Custom terms' },
  ];

  const applications = [
    { name: 'Locomotives', icon: Train, description: 'Diesel-electric hybrid systems' },
    { name: 'Metro Systems', icon: Gauge, description: 'Urban transit energy recovery' },
    { name: 'Maintenance Vehicles', icon: Shield, description: 'Track maintenance equipment' },
  ];

  const features = [
    'Custom engineering for specific rail applications',
    'Vibration and shock resistant design',
    'Fire-resistant enclosure (EN 45545 compliant)',
    'Regenerative braking energy capture',
    'Extreme temperature operation (-40°C to +65°C)',
    'Remote diagnostics and monitoring',
    'Modular architecture for easy maintenance',
    'Dedicated railway engineering support',
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1608461127691-760666cf8cd5?w=1920')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Products
          </Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Rail <span className="text-emerald-400">Systems</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mb-8">
            Custom-engineered Multi-Ion battery systems for locomotives, metro systems, and rail maintenance vehicles.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
          >
            Request Specifications <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Specs & Features */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Specifications</h2>
              <div className="grid grid-cols-2 gap-4">
                {specs.map((spec) => (
                  <div key={spec.label} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                    <p className="text-gray-500 text-sm mb-1">{spec.label}</p>
                    <p className="text-white font-semibold">{spec.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Key Features</h2>
              <div className="space-y-4">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <p className="text-gray-300">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Applications</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {applications.map((app) => {
              const Icon = app.icon;
              return (
                <div key={app.name} className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700 text-center">
                  <div className="w-16 h-16 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-white font-semibold text-xl mb-2">{app.name}</h3>
                  <p className="text-gray-500">{app.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Need a custom rail solution?</h2>
          <p className="text-gray-400 text-lg mb-8">
            Our railway engineering team can design systems for your specific requirements.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
          >
            Talk to Engineering <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RailSystemsPage;
